import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { formatCount } from "../Home/useAnimatedCount";
import {
  getProductDisplayName,
  getProductTokenCount,
  readNumericValue
} from "../../utils/getTokenProductUtils";
import {
  STATUS_ACTIVE,
  STATUS_ERROR,
  STATUS_EXPIRED,
  STATUS_NONE
} from "../../utils/subscriptionValidityUtils";

const statusClassName = (status) => {
  if (status === STATUS_ACTIVE) return "subscription-status-badge active";
  if (status === STATUS_EXPIRED) return "subscription-status-badge expired";
  if (status === STATUS_ERROR) return "subscription-status-badge error";
  return "subscription-status-badge none";
};

const SubscriptionStatusCard = ({
  validityChecking,
  validityStatus,
  expirationDate,
  validityError,
  selectedProduct,
  canPurchase
}) => {
  const { t } = useTranslation("common");

  const renderValiditySection = () => {
    if (validityChecking) {
      return (
        <p className="subscription-validity-message">{t("Checking subscription status...")}</p>
      );
    }

    if (validityStatus === STATUS_ERROR) {
      return (
        <>
          <div className="subscription-validity-row">
            <span className="subscription-validity-label">{t("Subscription Status")}</span>
            <span className={statusClassName(validityStatus)}>{t("Unknown")}</span>
          </div>
          <p className="subscription-validity-message error">
            {validityError || t("Unable to verify your subscription status. Please try again.")}
          </p>
        </>
      );
    }

    if (validityStatus === STATUS_ACTIVE) {
      return (
        <>
          <div className="subscription-validity-row">
            <span className="subscription-validity-label">{t("Subscription Status")}</span>
            <span className={statusClassName(validityStatus)}>{t("Active")}</span>
          </div>
          {expirationDate ? (
            <div className="subscription-validity-row">
              <span className="subscription-validity-label">{t("Subscription Expiration")}</span>
              <span className="subscription-validity-value">{expirationDate}</span>
            </div>
          ) : null}
          <p className="subscription-validity-message">
            {t("Your subscription is active.")}
          </p>
          <p className="subscription-validity-note">
            {t(
              "You cannot purchase another subscription until the current subscription expires."
            )}
          </p>
        </>
      );
    }

    if (validityStatus === STATUS_EXPIRED) {
      return (
        <>
          <div className="subscription-validity-row">
            <span className="subscription-validity-label">{t("Subscription Status")}</span>
            <span className={statusClassName(validityStatus)}>{t("Expired")}</span>
          </div>
          {expirationDate ? (
            <div className="subscription-validity-row">
              <span className="subscription-validity-label">{t("Subscription Expiration")}</span>
              <span className="subscription-validity-value">{expirationDate}</span>
            </div>
          ) : null}
          <p className="subscription-validity-message">
            {t("Your subscription has expired. Please renew your subscription.")}
          </p>
        </>
      );
    }

    if (validityStatus === STATUS_NONE) {
      return (
        <>
          <div className="subscription-validity-row">
            <span className="subscription-validity-label">{t("Subscription Status")}</span>
            <span className={statusClassName(validityStatus)}>{t("Not subscribed")}</span>
          </div>
          <p className="subscription-validity-message">
            {t("You do not have an active subscription.")}
          </p>
        </>
      );
    }

    return (
      <p className="subscription-validity-message">{t("Checking subscription status...")}</p>
    );
  };

  const showPlanDetails = canPurchase && selectedProduct;

  return (
    <div className="card subscription-validity-card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div className="card-title">{t("Subscription Status")}</div>
      </div>

      {renderValiditySection()}

      {showPlanDetails ? (
        <div className="subscription-plan-preview">
          <div className="subscription-validity-row">
            <span className="subscription-validity-label">{t("Selected plan")}</span>
            <span className="subscription-validity-value">
              {getProductDisplayName(selectedProduct)}
            </span>
          </div>
          <div className="subscription-validity-row">
            <span className="subscription-validity-label">{t("Billing period")}</span>
            <span className="subscription-validity-value">
              {readNumericValue(selectedProduct.expiration_timeframe, 12)} {t("months")}
            </span>
          </div>
          <div className="subscription-validity-row">
            <span className="subscription-validity-label">{t("Included tokens")}</span>
            <span className="subscription-validity-value val-green">
              +{formatCount(getProductTokenCount(selectedProduct))}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

SubscriptionStatusCard.propTypes = {
  validityChecking: PropTypes.bool,
  validityStatus: PropTypes.string,
  expirationDate: PropTypes.string,
  validityError: PropTypes.string,
  selectedProduct: PropTypes.object,
  canPurchase: PropTypes.bool
};

SubscriptionStatusCard.defaultProps = {
  validityChecking: false,
  validityStatus: null,
  expirationDate: null,
  validityError: "",
  selectedProduct: null,
  canPurchase: false
};

export default SubscriptionStatusCard;
